import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { Button, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFieldArray, useWatch } from 'react-hook-form';

import InputField from 'shared/components/forms/InputField/InputField';
import Select from 'shared/components/forms/Select';
import { Box } from 'styles/layout';

const FacilityIntegrationCodeMappingsTable = ({
  control,
  register,
  getFieldError,
  disabled,
  name = 'facilityIntegrationCodeMappings',
  consultTypePath = 'facilityIntegrationConfigurations.RECEIVE_ORDERS.consultTypes',
  consultTypeOptions = [],
}) => {
  const { fields, append, remove } = useFieldArray({ control, name });

  // Watch raw value from form
  const selectedConsultTypesRaw = useWatch({ control, name: consultTypePath });

  // Normalize once to avoid logical expressions in deps
  const selectedConsultTypes = React.useMemo(
    () =>
      Array.isArray(selectedConsultTypesRaw) ? selectedConsultTypesRaw : [],
    [selectedConsultTypesRaw],
  );

  // Build options limited to chosen consult types
  const serviceLineOptions = React.useMemo(() => {
    if (!selectedConsultTypes.length) return [];
    const allowed = new Set(selectedConsultTypes);
    return (consultTypeOptions || []).filter((opt) => allowed.has(opt.value));
  }, [selectedConsultTypes, consultTypeOptions]);

  const addRow = () => {
    append({
      orderId: '',
      orderText: '',
      services: [],
    });
  };

  return (
    <Box sx={{ pt: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item md={3} xs={12}>
              <Typography variant="caption" fontWeight="bold">
                Order ID
              </Typography>
            </Grid>
            <Grid item md={4} xs={12}>
              <Typography variant="caption" fontWeight="bold">
                Order Text
              </Typography>
            </Grid>
            <Grid item md={4} xs={12}>
              <Typography variant="caption" fontWeight="bold">
                Service Line(s)
              </Typography>
            </Grid>
            <Grid item md={1} xs={12} />
          </Grid>
        </Grid>
        {fields.map((field, idx) => (
          <Grid item xs={12} key={field.id}>
            <Grid container spacing={1} alignItems="center">
              <Grid item md={3} xs={12}>
                <InputField
                  {...register(`${name}.${idx}.orderId`, { disabled })}
                  placeholder="(optional)"
                  error={getFieldError(`${name}.${idx}.orderId`)}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <InputField
                  {...register(`${name}.${idx}.orderText`, { disabled })}
                  error={getFieldError(`${name}.${idx}.orderText`)}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Select
                  control={control}
                  name={`${name}.${idx}.services`}
                  options={serviceLineOptions}
                  multiple
                  disabled={disabled || !serviceLineOptions.length}
                  error={getFieldError(`${name}.${idx}.services`)}
                />
              </Grid>
              <Grid item md={1} xs={12}>
                <IconButton
                  aria-label="remove row"
                  onClick={() => remove(idx)}
                  disabled={disabled}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={addRow}
            disabled={disabled || !serviceLineOptions.length}
          >
            Add new
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

FacilityIntegrationCodeMappingsTable.propTypes = {
  control: PropTypes.shape({}).isRequired,
  register: PropTypes.func.isRequired,
  getFieldError: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  consultTypePath: PropTypes.string,
  consultTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ),
};

export default FacilityIntegrationCodeMappingsTable;
